import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { usePlatformPermissions } from '../../hooks/usePlatformPermissions';
import accountApi from '../../services/accountApi';

function deliveryMessage(delivery, { resent = false } = {}) {
  if (!delivery) return null;
  const provider = delivery.provider || '';
  if (delivery.status === 'sent') {
    if (provider === 'mailgun') {
      return resent
        ? 'Invitation email resent and accepted by Mailgun. Inbox delivery is not guaranteed by API acceptance alone.'
        : 'Invitation email accepted by Mailgun. Inbox delivery is not guaranteed by API acceptance alone.';
    }
    return resent
      ? 'Invitation email resent and accepted by the email provider.'
      : 'Invitation email accepted by the email provider.';
  }
  if (delivery.status === 'development_only') {
    return resent
      ? 'Invitation resent. Email delivery uses the development backend (not a real inbox).'
      : 'Invitation created. Email delivery uses the development backend (not a real inbox).';
  }
  if (delivery.status === 'failed') {
    const code = delivery.error_code || delivery.code;
    if (code === 'mailgun_not_configured' || code === 'smtp_not_configured') {
      return resent
        ? 'Invitation resend failed: email provider is not configured.'
        : 'Invitation created, but email provider is not configured.';
    }
    return resent
      ? `Invitation email delivery failed${code ? ` (${code})` : ''}. Previous invite link remains valid.`
      : `Invitation email delivery failed${code ? ` (${code})` : ''}.`;
  }
  return null;
}

function PlatformPartnerDetailPage() {
  const { partnerId } = useParams();
  const { hasPlatformPermission } = usePlatformPermissions();
  const canManage = hasPlatformPermission('platform.tenant.manage');
  const [detail, setDetail] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [bundle, setBundle] = useState('standard');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [devInviteToken, setDevInviteToken] = useState(null);
  const [devAcceptUrl, setDevAcceptUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, e] = await Promise.all([
        accountApi.getPlatformPartner(partnerId),
        accountApi.getPlatformPartnerEntitlements(partnerId),
      ]);
      setDetail(d);
      setEntitlements(e);
      setBundle(e?.entitlements?.capability_bundle || 'standard');
    } catch (err) {
      setError(err.message || 'Failed to load partner');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const saveEntitlements = async () => {
    if (!canManage) return;
    if (
      !window.confirm(
        'Changing the capability bundle may disable Partner features. Continue?'
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const e = await accountApi.updatePlatformPartnerEntitlements(partnerId, {
        capability_bundle: bundle,
      });
      setEntitlements(e);
      setMessage('Entitlements updated.');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update entitlements');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (invitationId) => {
    if (!canManage || !partnerId) return;
    setResending(invitationId);
    setError(null);
    setMessage(null);
    setDevInviteToken(null);
    setDevAcceptUrl(null);
    try {
      const result = await accountApi.resendPlatformPartnerInvitation(
        partnerId,
        invitationId
      );
      setDevInviteToken(result?.development_token || null);
      setDevAcceptUrl(result?.development_accept_url || null);
      setMessage(
        deliveryMessage(result?.delivery, { resent: true }) || 'Invitation resent.'
      );
      await load();
    } catch (err) {
      setError(err.message || 'Failed to resend invitation');
    } finally {
      setResending(null);
    }
  };

  const handleRevoke = async (invitationId) => {
    if (!canManage || !partnerId) return;
    if (
      !window.confirm(
        'Revoke this invitation? The previous invite link will stop working.'
      )
    ) {
      return;
    }
    setRevoking(invitationId);
    setError(null);
    setMessage(null);
    try {
      await accountApi.revokePlatformPartnerInvitation(partnerId, invitationId);
      setMessage('Invitation revoked.');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to revoke invitation');
    } finally {
      setRevoking(null);
    }
  };

  const partner = detail?.partner || detail?.tenant;
  const onboarding = detail?.onboarding || {};
  const ents = entitlements?.entitlements;
  const pendingInvitations = detail?.pending_invitations || [];

  return (
    <div>
      <p className="mb-2">
        <Link to="/platform/partners">← Partners</Link>
      </p>
      <h1 className="product-page-title">Partner Detail</h1>
      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}
      {devInviteToken && (
        <Alert variant="warning">
          Development-only invitation token (shown once; copy now):{' '}
          <code>{devInviteToken}</code>
        </Alert>
      )}
      {devAcceptUrl && (
        <Alert variant="warning">
          Development-only accept URL: <a href={devAcceptUrl}>{devAcceptUrl}</a>
        </Alert>
      )}
      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}
      {!loading && partner && (
        <>
          <div className="product-card mb-3">
            <h2 className="h5">{partner.name}</h2>
            <p className="mb-1">
              <strong>Slug:</strong> <span className="mono">{partner.slug}</span>
            </p>
            <p className="mb-1">
              <strong>Status:</strong> {partner.status}
            </p>
            <p className="mb-1">
              <strong>Type:</strong> {partner.partner_type || partner.tenant_type}
            </p>
            <p className="mb-1">
              <strong>Service tier:</strong> {partner.service_tier || '—'}
            </p>
            <p className="mb-1">
              <strong>Billing:</strong> {partner.billing?.billing_provider || '—'} /{' '}
              {partner.billing?.billing_status || '—'}
              {partner.billing?.is_mock ? ' (mock)' : ''}
            </p>
            <p className="mb-1">
              <strong>Interval:</strong> {partner.billing?.billing_interval || '—'}
            </p>
            <p className="mb-0">
              <strong>Effective tier:</strong>{' '}
              {partner.billing?.effective_tier || partner.selected_tier || '—'}
            </p>
          </div>

          <div className="product-card mb-3">
            <h3 className="h6">Entitlements</h3>
            <p className="mb-1">
              <strong>Effective bundle:</strong>{' '}
              {ents?.capability_bundle || onboarding.capability_bundle || '—'}
            </p>
            <p className="mb-2">
              <strong>Capabilities:</strong>{' '}
              {(ents?.capabilities || onboarding.capabilities || []).join(', ') || '—'}
            </p>
            {canManage && (
              <Form className="row g-2 align-items-end mb-3">
                <div className="col-md-6">
                  <Form.Label>Change capability bundle</Form.Label>
                  <Form.Select value={bundle} onChange={(e) => setBundle(e.target.value)}>
                    {['standard', 'developer', 'enterprise', 'internal', 'fixture'].map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-3">
                  <Button variant="outline-primary" disabled={saving} onClick={saveEntitlements}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </Form>
            )}
            {(entitlements?.quotas || []).length > 0 && (
              <Table responsive size="sm" className="product-table">
                <thead>
                  <tr>
                    <th>Quota</th>
                    <th>Current</th>
                    <th>Limit</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {entitlements.quotas
                    .filter((q) => q.enforced)
                    .map((q) => (
                      <tr key={q.code}>
                        <td className="mono small">{q.code}</td>
                        <td>{q.current}</td>
                        <td>{q.limit}</td>
                        <td>{q.status}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )}
          </div>

          <div className="product-card mb-3">
            <h3 className="h6">Onboarding</h3>
            <p className="mb-1">
              <strong>Capability bundle:</strong> {onboarding.capability_bundle || '—'}
            </p>
            <p className="mb-0 text-muted">{onboarding.description || ''}</p>
          </div>

          <div className="product-card mb-3">
            <h3 className="h6">Owner</h3>
            {(detail.owner?.memberships || []).length === 0 &&
            (detail.owner?.pending_invitations || []).length === 0 ? (
              <p className="mb-0 text-muted">No owner recorded.</p>
            ) : (
              <>
                {(detail.owner?.memberships || []).map((m) => (
                  <p key={m.id} className="mb-1">
                    {m.email} · {m.status}
                  </p>
                ))}
                {(detail.owner?.pending_invitations || []).map((i) => (
                  <p key={i.id} className="mb-1 text-muted">
                    Pending owner invite: {i.email}
                  </p>
                ))}
              </>
            )}
          </div>

          {detail.counts && (
            <div className="product-card mb-3">
              <h3 className="h6">Counts</h3>
              <ul className="mb-0">
                <li>Members: {detail.counts.members}</li>
                <li>Pending invitations: {detail.counts.pending_invitations}</li>
                <li>Projects: {detail.counts.projects}</li>
                <li>Jobs: {detail.counts.jobs}</li>
                <li>API keys (non-revoked metadata): {detail.counts.api_keys}</li>
              </ul>
            </div>
          )}

          <div className="product-card mb-3">
            <h3 className="h6">Pending invitations</h3>
            {pendingInvitations.length === 0 ? (
              <p className="mb-0 text-muted">No pending invitations.</p>
            ) : (
              <Table responsive size="sm" className="product-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Delivery</th>
                    <th>Last sent</th>
                    <th>Expires</th>
                    {canManage && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {pendingInvitations.map((i) => (
                    <tr key={i.id}>
                      <td>{i.email}</td>
                      <td>{i.role}</td>
                      <td className="small">
                        {i.delivery?.status || '—'}
                        {i.send_count ? ` · ${i.send_count}×` : ''}
                      </td>
                      <td className="small">
                        {i.last_sent_at
                          ? new Date(i.last_sent_at).toLocaleString()
                          : '—'}
                      </td>
                      <td>
                        {i.expires_at
                          ? new Date(i.expires_at).toLocaleString()
                          : '—'}
                      </td>
                      {canManage && (
                        <td className="text-nowrap">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            disabled={resending === i.id || revoking === i.id}
                            onClick={() => handleResend(i.id)}
                          >
                            {resending === i.id ? 'Resending…' : 'Resend'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={resending === i.id || revoking === i.id}
                            onClick={() => handleRevoke(i.id)}
                          >
                            {revoking === i.id ? 'Revoking…' : 'Revoke'}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {!canManage && pendingInvitations.length > 0 && (
              <p className="small text-muted mb-0 mt-2">
                Resend and revoke require platform Partner manage permission.
              </p>
            )}
          </div>

          <div className="product-card mb-3">
            <h3 className="h6">Partner Members</h3>
            <Table responsive size="sm" className="product-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(detail.members || []).map((m) => (
                  <tr key={m.id}>
                    <td>{m.email}</td>
                    <td>{m.role}</td>
                    <td>{m.status}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {(detail.recent_audit || []).length > 0 && (
            <div className="product-card">
              <h3 className="h6">Recent activity</h3>
              <Table responsive size="sm" className="product-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.recent_audit.map((e) => (
                    <tr key={e.id}>
                      <td className="mono">{e.action}</td>
                      <td>{e.actor_email || e.actor_id || '—'}</td>
                      <td>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PlatformPartnerDetailPage;
